import { ReactNode } from 'react'

interface TypographyProps {
  children: ReactNode
  className?: string
  as?: keyof JSX.IntrinsicElements
}

interface TypographyVariantProps extends TypographyProps {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'body1' | 'body2' | 'caption' | 'overline'
}

// تیترها با «Markazi Text» — حس مجله‌ای و شخصی؛ متن‌ها با وزیرمتن برای خوانایی
const variantClasses = {
  h1: 'font-display text-5xl sm:text-6xl md:text-7xl font-medium text-gray-900 leading-[1.2] tracking-tight',
  h2: 'font-display text-4xl md:text-5xl font-medium text-gray-900 leading-snug',
  h3: 'font-display text-2xl md:text-3xl font-medium text-gray-900',
  h4: 'font-display text-xl md:text-2xl font-medium text-gray-800',
  h5: 'text-lg md:text-xl font-medium text-gray-800',
  body1: 'text-base md:text-lg text-gray-700 leading-9',
  body2: 'text-sm md:text-base text-gray-600 leading-8',
  caption: 'text-xs md:text-sm text-gray-500',
  overline: 'text-xs font-semibold uppercase tracking-wider text-primary-500',
}

const Typography = ({
  children,
  variant = 'body1',
  className = '',
  as: Component = 'div'
}: TypographyVariantProps) => {
  const baseClasses = variantClasses[variant]

  return (
    <Component className={`${baseClasses} ${className}`}>
      {children}
    </Component>
  )
}

// Specific typography components
const H1 = (props: TypographyProps) => <Typography {...props} variant="h1" as="h1" />
const H2 = (props: TypographyProps) => <Typography {...props} variant="h2" as="h2" />
const H3 = (props: TypographyProps) => <Typography {...props} variant="h3" as="h3" />
const H4 = (props: TypographyProps) => <Typography {...props} variant="h4" as="h4" />
const H5 = (props: TypographyProps) => <Typography {...props} variant="h5" as="h5" />
const Body1 = (props: TypographyProps) => <Typography {...props} variant="body1" as="p" />
const Body2 = (props: TypographyProps) => <Typography {...props} variant="body2" as="p" />
const Caption = (props: TypographyProps) => <Typography {...props} variant="caption" as="span" />
const Overline = (props: TypographyProps) => <Typography {...props} variant="overline" as="span" />

export {
  Typography,
  H1,
  H2,
  H3,
  H4,
  H5,
  Body1,
  Body2,
  Caption,
  Overline,
}
